// MemberPickerModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useBase } from 'context/BaseContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { buildBaseMemberProfile } from 'utilities/BaseMembers';

// Services
import { subscribeToBaseMembers } from 'services/bases/BaseMemberService';

// Hooks
import useNodesByKeysQuery from 'hooks/useNodesByKeysQuery';

// CSS
import './MemberPickerModal.css';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import ProfilePhoto from 'components/profilephoto/ProfilePhoto';
import TextInput from 'components/textinput/TextInput';

const getPersonKey = (person) => String(person?.userKey || person?.key || '').trim();

const normalizeText = (value) => String(value || '').trim();

const getSafePersonName = (person, fallback = '') => {
    const key = getPersonKey(person);
    const candidates = [
        person?.name,
        person?.data_profile?.name,
        person?.title,
        person?.email,
    ];

    const valid = candidates.find((candidate) => {
        const text = normalizeText(candidate);
        return text && text !== key;
    });

    return normalizeText(valid) || fallback;
};

const getSafePersonSubtitle = (person) => {
    const key = getPersonKey(person);
    const email = normalizeText(person?.email);

    if (email && email !== key) return email;

    return '';
};

const normalizePeople = (value) => {
    const list = Array.isArray(value) ? value : value ? [value] : [];
    const map = new Map();

    list.forEach((entry) => {
        if (!entry) return;
        const key = getPersonKey(entry);
        if (!key) return;

        map.set(key, {
            key,
            userKey: key,
            name: getSafePersonName(entry),
            title: normalizeText(entry?.title) || getSafePersonName(entry),
            email: entry?.email || entry?.data_profile?.email || '',
            coverUrl: entry?.coverUrl || '',
            role: entry?.role || 'member',
            data_profile: {
                name: getSafePersonName(entry),
                email: entry?.email || entry?.data_profile?.email || '',
            },
        });
    });

    return map;
};

const buildPersonFromMember = (member, userNode) => {
    const profile = buildBaseMemberProfile(member, userNode);
    const userKey = String(profile?.userKey || profile?.key || '').trim();
    const name = getSafePersonName(profile);
    const email = profile?.email || '';

    return {
        key: userKey,
        userKey,
        name,
        title: name,
        email,
        role: profile?.role || 'member',
        coverUrl: profile?.coverUrl || '',
        data_profile: {
            name,
            email,
        },
    };
};

/**
 * MemberPickerModal
 *
 * Modal for selecting base members in any base-scoped flow.
 *
 * @param {object} node - Optional node used to derive base scope.
 * @param {string} baseKey - Optional explicit base scope.
 * @param {string} label - The label to display in the modal header.
 * @param {object[]} value - The current selected member rows.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} setOpen - Function to set the open state.
 * @param {function} onChange - Callback to persist the selected members.
 * @param {function} onChangeAsync - Optional async save handler; when set, used instead of onChange.
 * @param {function} confirmEnabled - Optional validator for selected people before save.
 * @param {string} confirmLabel - Optional confirm button label.
 * @param {boolean} isSaving - Whether an async save is in progress.
 * @param {string} submitErrorMessage - Optional error shown below the picker list.
 *
 * @returns {ReactNode} MemberPickerModal component.
 */
const MemberPickerModal = ({
    node,
    baseKey,
    label,
    value,
    isOpen,
    setOpen,
    onChange,
    onChangeAsync,
    confirmEnabled,
    confirmLabel = 'Save',
    isSaving = false,
    submitErrorMessage = '',
}) => {
    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- CONTEXT ----- //

    const { activeBase } = useBase();
    const { theme } = useTheme();

    // ----- STATE ----- //

    const [selectedMap, setSelectedMap] = useState(() => normalizePeople(value));
    const [searchValue, setSearchValue] = useState('');
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const resolvedBaseKey = baseKey || node?.baseKey || activeBase?.key || null;

    // ----- MEMOS ----- //

    const memberNodeKeys = useMemo(() => (
        Array.from(new Set(
            (Array.isArray(members) ? members : [])
                .map((member) => String(member?.userKey || member?.key || member?.id || '').trim())
                .filter(Boolean)
        ))
    ), [members]);

    // ----- HOOKS ----- //

    const { nodesByKey: memberUsersByKey } = useNodesByKeysQuery(memberNodeKeys, {
        enabled: isOpen && memberNodeKeys.length > 0,
    });

    // ----- EFFECTS ----- //

    useEffect(() => {
        if (!isOpen) return;
        setSelectedMap(normalizePeople(value));
        setSearchValue('');
    }, [isOpen, value]);

    useEffect(() => {
        if (!isOpen || !resolvedBaseKey) {
            setMembers([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setErrorMessage('');

        const unsubscribe = subscribeToBaseMembers(
            resolvedBaseKey,
            (rows) => {
                setMembers(Array.isArray(rows) ? rows : []);
                setLoading(false);
            },
            (error) => {
                setErrorMessage(error?.message || 'Failed to load base members.');
                setMembers([]);
                setLoading(false);
            }
        );

        return () => {
            unsubscribe?.();
        };
    }, [isOpen, resolvedBaseKey]);

    // ----- MEMOS ----- //

    const memberPeople = useMemo(() => {
        const list = Array.isArray(members) ? members : [];

        return list
            .map((member) => {
                const userKey = String(member?.userKey || member?.key || member?.id || '').trim();
                if (!userKey) return null;
                const userNode = memberUsersByKey.get(userKey) || null;
                return buildPersonFromMember(member, userNode);
            })
            .filter(Boolean)
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    }, [members, memberUsersByKey]);

    const filteredPeople = useMemo(() => {
        const query = String(searchValue || '').trim().toLowerCase();
        if (!query) return memberPeople;

        return memberPeople.filter((person) => {
            const haystack = [
                person?.name,
                person?.title,
                person?.email,
                person?.userKey,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return haystack.includes(query);
        });
    }, [memberPeople, searchValue]);

    // ----- HANDLERS ----- //

    /**
     * handleToggle
     *
     * Toggles the current selection or state.
     *
     * @param {unknown} person - person.
     */
    const handleToggle = (person) => {
        const key = getPersonKey(person);
        if (!key) return;

        setSelectedMap((prev) => {
            const next = new Map(prev);

            if (next.has(key)) {
                next.delete(key);
            } else {
                next.set(key, person);
            }

            return next;
        });
    };

  /**
   * handleSave
   *
   * Persists the current changes.
   * @returns {Promise<void>} Resolves when the handler completes.
   */
  const handleSave = async () => {
    const selectedPeople = Array.from(selectedMap.values());

        if (typeof confirmEnabled === 'function' && !confirmEnabled(selectedPeople)) {
            return;
        }

        if (typeof onChangeAsync === 'function') {
            try {
                await onChangeAsync(selectedPeople);
            } catch (_error) {
                // Caller surfaces submit errors via submitErrorMessage.
            }
            return;
        }

        onChange?.(selectedPeople);
        setOpen(false);
    };

    // ----- MEMOS ----- //

    const selectedPeople = useMemo(() => Array.from(selectedMap.values()), [selectedMap]);
    const canConfirm = typeof confirmEnabled === 'function'
        ? confirmEnabled(selectedPeople)
        : true;

    const hasBase = Boolean(resolvedBaseKey);

    // ----- RENDER ----- //

    return (
        <Modal
          iconKey="group"
          title={label || 'Edit'}
          isOpen={isOpen}
          width={isMobile ? '100%' : '520px'}
          height="auto"
          onClose={() => setOpen(false)}
        >
            <ModalForm>
            <div className="member-picker-modal-container">
                <TextInput
                  value={searchValue}
                  placeholder="Search people..."
                  startIconKey="search"
                  startIconColor={theme.foregroundMuted}
                  iconSize={20}
                  autoFocus
                  onChange={(event) => setSearchValue(event.target.value)}
                />

                {/* LIST */}
                <div
                  className="member-picker-modal-list"
                >

                    {/* EMPTY */}
                    {!hasBase ? (
                        <div
                          className="member-picker-modal-empty"
                        >
                            No active base.
                        </div>
                    ) : null}

                    {/* EMPTY */}
                    {hasBase && !loading && errorMessage ? (
                        <div
                          className="member-picker-modal-empty"
                        >
                            {errorMessage}
                        </div>
                    ) : null}

                    {/* EMPTY */}
                    {hasBase && !loading && !errorMessage && filteredPeople.length === 0 ? (
                        <div
                          className="member-picker-modal-empty"
                        >
                            No people
                        </div>
                    ) : null}

                    {hasBase && !loading && !errorMessage ? filteredPeople.map((person) => {
                        const key = getPersonKey(person);
                        const checked = selectedMap.has(key);

                        return (
                            <div
                              className="member-picker-modal-row"
                              style={{
                                backgroundColor: checked ? theme.backgroundMuted : 'transparent',
                              }}
                              key={key}
                              onClick={() => handleToggle(person)}
                            >
                                <Checkbox
                                  value={checked}
                                  size={30}
                                  onChange={(event) => {
                                        event?.stopPropagation?.();
                                        handleToggle(person);
                                    }}
                                />

                                <ProfilePhoto
                                  node={{
                                        key,
                                        name: person?.name,
                                        title: person?.name,
                                        coverUrl: person?.coverUrl || null,
                                    }}
                                  size={28}
                                  borderWidth={1}
                                />

                                {/* META */}
                                <div className="member-picker-modal-meta">

                                    {/* NAME */}
                                    <div
                                      className="member-picker-modal-name"
                                    >
                                        {getSafePersonName(person)}
                                    </div>

                                    {/* SUB */}
                                    <div
                                      className="member-picker-modal-sub"
                                    >
                                        {getSafePersonSubtitle(person)}
                                    </div>
                                </div>
                            </div>
                        );
                    }) : null}
                </div>

                {/* SUMMARY */}
                <div
                  className="member-picker-modal-summary"
                >
                    {selectedMap.size} selected
                </div>

                {/* EMPTY */}
                {submitErrorMessage ? (
                    <div
                      className="member-picker-modal-empty"
                    >
                        {submitErrorMessage}
                    </div>
                ) : null}
            </div>
            </ModalForm>

            <ModalButtons>
                <Button
                  label="Cancel"
                  enabled={!isSaving}
                  onClick={() => setOpen(false)}
                />

                <Button
                  iconKey="check"
                  label={confirmLabel}
                  enabled={canConfirm && !isSaving}
                  onClick={handleSave}
                />
            </ModalButtons>
        </Modal>
    );
};

// Dev Tools
MemberPickerModal.displayName = 'MemberPickerModal';

export default MemberPickerModal;
